import {
  Button,
  ButtonGroup,
  Flex,
  StackDivider,
  VStack,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import BigModal from 'web-react-ui/src/BigModal/BigModal';
import Post from 'shared/src/interfaces/Post.interface';
import PageHeader from 'web-react-ui/src/chakra/page/PageHeader';
import SimpleForm2 from 'web-react-ui/src/reactFinalForm/SimpleForm2';
import FormError from 'web-react-ui/src/reactFinalForm/FormError';
import View from 'web-react-ui/src/components/layout/View';
import { getPostEditPageTitle } from '../getPostPageTitle';
import CaptionSection from './CaptionSection';
import ImageVariablesSection from './ImageVariablesSection';
import ImageCustomerSection from './ImageCustomerSection';
import postVariablesToFormVariables from './postVariablesToFormVariables';
import ScheduledDateSection from './ScheduledDateSection';

type PostEditProps = {
  onSave: ({ message, scheduledDate, imageFormVariables }: {
    message: Post['output']['message'],
    scheduledDate: Post['scheduledDate'],
    imageFormVariables: Post['imageFormVariables'],
  }) => Promise<void>;
  onClose: () => void;
  post: Post;
  admin?: boolean;
};

const PostEdit = ({ post, admin }: Omit<PostEditProps, 'onSave' | 'onClose'>) => {
  const canEditScheduledDate = ['scheduled', 'draft'].includes(post.state);
  const canEditMessage = ['post', 'reel'].includes(post.contentType);
  const canEditVariables = admin && post.canModify;

  return (
    <Flex
      direction="column"
      gap="1.5em"
      w="2/3"
      grow={2}
      alignItems="start"
    >
      <FormError />
      <VStack spacing={8} divider={<StackDivider />} w="100%" align="stretch">
        {canEditScheduledDate && <ScheduledDateSection post={post} />}
        {canEditVariables
          ? <ImageVariablesSection post={post} />
          : <ImageCustomerSection post={post} />
        }
        {canEditMessage && <CaptionSection />}
      </VStack>
    </Flex>
  );
};

const PostEditWrapper = ({ post, onSave, onClose, admin }: PostEditProps) => {
  const handleSubmit = async (postUpdates: Post, form: any) => {
    const message = postUpdates.output.message || undefined; // can't send empty message

    let scheduledDate;
    const initialValues = form.getState().initialValues;
    if (postUpdates.scheduledDate !== initialValues.scheduledDate) {
      scheduledDate = postUpdates.scheduledDate;
      // The date picker is restricted to the future _at the time it was opened_.
      // So if the date is slightly in the past, it's because the user didn't submit immediately.
      // So we clamp the time to "now"
      if (new Date(postUpdates.scheduledDate).valueOf() < Date.now()) {
        scheduledDate = new Date().toISOString();
      }
    }

    return onSave({
      message,
      scheduledDate,
      imageFormVariables: postUpdates.imageFormVariables,
    });
  };

  const postWithVariables = useMemo(
    () => postVariablesToFormVariables(post),
    [post],
  );

  return (
    <BigModal open>
      <SimpleForm2 onSubmit={handleSubmit} initialValues={postWithVariables}>
        <BigModal.Contents>
          <BigModal.Header className="p1">
            <PageHeader
              controls={
                <ButtonGroup spacing={2}>
                  <Button onClick={onClose}>Cancel</Button>
                  <Button colorScheme="blue" type="submit">Save</Button>
                </ButtonGroup>
              }
              boxProps={{ py: 0 }}
            >
              {getPostEditPageTitle(post)}
            </PageHeader>
          </BigModal.Header>
          <BigModal.Body>
            <View>
              <View.Section>
                <PostEdit post={postWithVariables} admin={admin} />
              </View.Section>
            </View>
          </BigModal.Body>
        </BigModal.Contents>
      </SimpleForm2>
    </BigModal>
  );
};

export default PostEditWrapper;
